import { html, type TemplateResult } from 'lit-html';
import { api, get } from '../api.js';
import { act, app, confirmDialog, copy, isAdmin, isOwner, type Page } from '../app.js';
import { pill, when } from '../format.js';
import { connectorHref, type ConnectorTab, type ConnectorUi } from '../connector-ui.js';
import type { Connector } from '../types.js';
import { forwardingTab, loadForwarding } from './forwarding.js';
import { stannpUi } from './stannp.js';

/** Connector-specific tabs, by connector name. Connectors not listed here get the Settings tab only. */
const CONNECTOR_UI: Record<string, ConnectorUi> = {
  stannp: stannpUi,
};

let list: Connector[] = [];
let current: Connector | null = null;
/** The open tab: null = Settings. */
let tab: ConnectorTab | null = null;

/** param: '' (list) | '{name}' (Settings) | '{name}/{tab}/{rest…}' */
async function load(param: string): Promise<void> {
  current = null;
  tab = null;
  if (!param) {
    list = (await get<{ connectors: Connector[] }>('/connectors')).connectors;
    return;
  }
  const [name = '', tabId = '', ...rest] = param.split('/');
  const ui = CONNECTOR_UI[name];
  tab = tabId === 'forwarding' ? forwardingTab : (ui?.tabs.find((t) => t.id === tabId) ?? null);
  const [c] = await Promise.all([
    get<{ connector: Connector }>(`/connectors/${encodeURIComponent(name)}`),
    tab === forwardingTab ? loadForwarding(name) : tab ? tab.load(rest.join('/')) : ui?.loadSettings?.(),
  ]);
  current = c.connector;
}
const reload = () => load(app.routeParam);

function listView(): TemplateResult {
  return html`
    <h1>Connectors</h1>
    <p class="hint">Each connector is one outside service, reached at <code>${app.me.public_url}/{name}</code>. Connectors start switched off.</p>
    <div class="grid two">${list.map(
      (c) => html`<a class="card link-card" href="#/connectors/${c.name}">
        <div class="row-between"><h2>${c.display_name}</h2>${c.enabled ? pill('On', 'positive') : pill('Off')}</div>
        <p class="muted">${c.description}</p><p><code>${c.url}</code></p></a>`,
    )}</div>`;
}

function settingsPanel(c: Connector): TemplateResult {
  const admin = isAdmin();
  const toggle = async (key: string, value: boolean) => {
    await act(() => api('PATCH', `/connectors/${c.name}`, { settings: { [key]: value }, version: c.version }), 'Setting saved', reload);
  };
  if (!c.settings.fields.length) return html``;
  return html`<section class="panel"><h2>Settings</h2>
    ${c.settings.fields.map(
      (f) => html`<div class="switch">
        ${f.type === 'boolean'
          ? html`<input type="checkbox" class="toggle" aria-label=${f.label} .checked=${c.settings.values[f.key] === true} ?disabled=${!admin}
              @change=${(e: Event) => toggle(f.key, (e.target as HTMLInputElement).checked)}>`
          : ''}
        <span><b>${f.label}</b><br><span class="muted">${f.description}</span></span>
      </div>`,
    )}</section>`;
}

function secretsPanel(c: Connector): TemplateResult {
  const owner = isOwner();
  const save = async (e: Event, name: string, label: string) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const value = (form.elements.namedItem('value') as HTMLInputElement).value;
    if (await act(() => api('PUT', `/connectors/${c.name}/secrets/${name}`, { value }), `${label} saved`, reload)) form.reset();
  };
  const clear = async (name: string, label: string) => {
    const ok = await confirmDialog({ title: `Remove the ${label}?`, body: `${c.display_name} calls that need it will fail until a new one is set.`, confirmLabel: 'Remove', tone: 'danger' });
    if (ok) await act(() => api('PUT', `/connectors/${c.name}/secrets/${name}`, { value: '' }), `${label} removed`, reload);
  };
  return html`<section class="panel"><h2>Credentials</h2>
    <p class="hint">Stored in AWS Secrets Manager. They can be replaced here but never shown again${owner ? '' : ' (owners only)'}.</p>
    <div class="card">${c.secrets.map(
      (s) => html`<div class="secret-row">
        <div class="what"><b>${s.label}</b> ${s.configured ? pill('Set', 'positive') : s.required ? pill('Not set', 'warning') : pill('Not set')}<p>${s.description}</p></div>
        ${owner
          ? html`<form @submit=${(e: Event) => save(e, s.name, s.label)}>
              <input name="value" type="password" autocomplete="off" required placeholder=${s.configured ? 'Replace…' : 'Paste value'} aria-label=${s.label}>
              <button class="btn">${s.configured ? 'Replace' : 'Save'}</button>
              ${s.configured ? html`<button type="button" class="btn quiet" @click=${() => clear(s.name, s.label)}>Remove</button>` : ''}
            </form>`
          : ''}
      </div>`,
    )}</div></section>`;
}

function scopesPanel(c: Connector): TemplateResult {
  return html`<details class="panel"><summary><h2>Permissions an API key can have</h2></summary>
    <div class="table-wrap mt-4"><table>
      <thead><tr><th>Scope</th><th>Allows</th></tr></thead>
      <tbody>${c.scopes.map((s) => html`<tr><td><b>${s.label}</b><br><code class="id">${s.id}</code></td><td>${s.description}</td></tr>`)}</tbody>
    </table></div></details>`;
}

function header(c: Connector): TemplateResult {
  // Connector-specific tabs, then the platform's Forwarding tab for connectors that emit events.
  const tabs = [...(CONNECTOR_UI[c.name]?.tabs ?? []), ...(c.events.length ? [forwardingTab] : [])];
  const link = (id: string | null, label: string) => {
    const active = (tab?.id ?? null) === id;
    return html`<a href=${connectorHref(c.name, id ?? '')} class=${active ? 'active' : ''} aria-current=${active ? 'page' : 'false'}>${label}</a>`;
  };
  return html`
    <p class="crumb"><a href="#/connectors">Connectors</a></p>
    <div class="row-between page-head"><h1>${c.display_name}</h1>${c.enabled ? pill('On', 'positive') : pill('Off')}</div>
    <nav class="tabs" aria-label="${c.display_name} sections">${link(null, 'Settings')}${tabs.map((t) => link(t.id, t.label))}</nav>`;
}

function settingsTab(c: Connector): TemplateResult {
  const admin = isAdmin();
  const setEnabled = async (enabled: boolean) => {
    if (!enabled) {
      const ok = await confirmDialog({
        title: `Switch ${c.display_name} off?`,
        body: 'Every call to it gets "connector_disabled" (HTTP 503) until it is switched back on. Mail already held keeps waiting.',
        confirmLabel: 'Switch off',
        tone: 'danger',
      });
      if (!ok) return app.rerender();
    }
    await act(() => api('PATCH', `/connectors/${c.name}`, { enabled, version: c.version }), enabled ? `${c.display_name} is on` : `${c.display_name} is off`, reload);
  };
  return html`
    <p class="hint">${c.description}</p>
    <div class="card">
      <div class="switch">
        <input type="checkbox" class="toggle" aria-label="Connector on" .checked=${c.enabled} ?disabled=${!admin} @change=${(e: Event) => setEnabled((e.target as HTMLInputElement).checked)}>
        <span><b>${c.enabled ? 'Switched on' : 'Switched off'}</b><br><span class="muted">${c.updated_by ? `Last changed ${when(c.updated_at)} by ${c.updated_by}` : 'Never switched on yet.'}</span></span>
      </div>
      <p>Base URL: <code>${c.url}</code> <button class="btn quiet compact" @click=${() => copy(c.url)}>Copy</button></p>
    </div>
    ${CONNECTOR_UI[c.name]?.settings?.(c) ?? ''}
    ${settingsPanel(c)}
    ${secretsPanel(c)}
    ${scopesPanel(c)}`;
}

export const connectorsPage: Page = {
  title: 'Connectors',
  load,
  view() {
    if (!current) return listView();
    return html`${header(current)}${tab ? tab.view(current) : settingsTab(current)}`;
  },
};
